import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
const topics = [{
  id: 'guide',
  title: 'AC installation guide Coventry'
}, {
  id: 'daikin',
  title: 'Daikin vs generic AC units'
}, {
  id: 'retail',
  title: 'Choosing AC for retail'
}, {
  id: 'toshiba',
  title: 'Toshiba AC benefits'
}, {
  id: 'why-us',
  title: 'Why choose AccuTemp'
}];
export function InstallationGuide() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-1644">
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-1645">
        <div className="container mx-auto px-6" data-id="element-1646">
          <div className="max-w-4xl" data-id="element-1647">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-1648">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-1649">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-1650" />
              <span className="text-primary-dark" data-id="element-1651">Installation Guide</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-1652">
              Commercial AC installation guide
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1653">
              Everything you need to know about sizing, selecting, and
              installing commercial air conditioning systems in the UK.
              Published April 2026.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-1654">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1655">
          <div className="lg:hidden" data-id="element-1656">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-1657">
              <span data-id="element-1658">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-1659" /> : <Menu className="w-5 h-5" data-id="element-1660" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-1661">
                <nav className="flex flex-col gap-3" data-id="element-1662">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-1663">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          <aside className="hidden lg:block w-64 shrink-0" data-id="element-1664">
            <div className="sticky top-32" data-id="element-1665">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-1666">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-1667">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-1668">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-1669" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-1670">
            {/* Topic 1 */}
            <section id="guide" className="mb-20 scroll-mt-32" data-id="element-1671">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1672">
                AC installation guide Coventry
              </h2>
              <p data-id="element-1673">
                Commercial AC installation starts with an accurate site survey.
                We calculate the required BTU based on square meterage,
                occupancy, heat-generating equipment, and solar gain.
              </p>
              <p data-id="element-1674">
                A professional F-Gas installation ensures your system operates
                efficiently and qualifies for extended warranties (up to 16
                years with Daikin). Avoid DIY or uncertified installers—poorly
                installed systems typically fail within 5 years due to
                refrigerant leaks or compressor strain.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1675">
                Steps to install
              </h3>
              <ol className="list-decimal pl-6 mb-8 space-y-2" data-id="element-1676">
                <li data-id="element-1677">Site survey and heat load calculation.</li>
                <li data-id="element-1678">System selection (e.g., Daikin or Toshiba VRV/VRF).</li>
                <li data-id="element-1679">Pipework and refrigerant fitting.</li>
                <li data-id="element-1680">Pressure testing and commissioning.</li>
              </ol>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1681">
                <h4 className="text-xl font-serif mb-4" data-id="element-1682">
                  Need a free site survey?
                </h4>
                <p className="mb-6 text-base" data-id="element-1683">
                  Get an accurate quote for your commercial premises in
                  Coventry.
                </p>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1684">
                  Book free survey Coventry
                  <ChevronRight className="w-4 h-4" data-id="element-1685" />
                </Link>
              </div>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1686">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1687">Installation FAQs</h3>
                <div className="space-y-4" data-id="element-1688">
                  {[{
                  q: 'What is the typical timeline for installation?',
                  a: 'Most standard commercial installations take 1-3 days per site, depending on complexity and access.'
                }, {
                  q: 'What warranty do you offer?',
                  a: 'As a Daikin D1+ Premier Partner, we can offer up to 16 years warranty on Daikin installations.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1689">
                      <button onClick={() => setOpenFaqId(openFaqId === `guide-${i}` ? null : `guide-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1690">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1691">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1692">
                          {openFaqId === `guide-${i}` ? <Minus className="w-5 h-5" data-id="element-1693" /> : <Plus className="w-5 h-5" data-id="element-1694" />}
                        </span>
                      </button>
                      {openFaqId === `guide-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1695">
                          <p itemProp="text" data-id="element-1696">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 2 */}
            <section id="daikin" className="mb-20 scroll-mt-32" data-id="element-1697">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1698">
                Daikin vs generic AC units
              </h2>
              <p data-id="element-1699">
                Investing in premium brands like Daikin offers significant
                long-term ROI. Daikin units last 12-16 years thanks to advanced
                inverter technology, whereas generic brands often fail in 5-7
                years due to poor compressor quality.
              </p>
              <p data-id="element-1700">
                Efficiency is another major factor: Daikin systems frequently
                achieve A+++ ratings, saving up to 30% on energy bills compared
                to standard B-rated generic units.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1701">
                Comparison table
              </h3>
              <div className="overflow-x-auto mb-8" data-id="element-1702">
                <table className="w-full text-left border-collapse" data-id="element-1703">
                  <thead data-id="element-1704">
                    <tr className="bg-[#E8F0F8]" data-id="element-1705">
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1706">
                        Feature
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1707">
                        Daikin
                      </th>
                      <th className="p-4 font-medium text-primary-dark border-b border-primary-dark/10" data-id="element-1708">
                        Generic
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1709">
                    <tr data-id="element-1710">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1711">
                        Warranty
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1712">
                        Up to 16 years
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1713">
                        1-5 years
                      </td>
                    </tr>
                    <tr data-id="element-1714">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1715">
                        Noise Level
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1716">
                        &lt;20dB (Whisper quiet)
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1717">
                        30+dB (Noticeable hum)
                      </td>
                    </tr>
                    <tr data-id="element-1718">
                      <td className="p-4 border-b border-primary-dark/10 font-medium" data-id="element-1719">
                        Efficiency
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1720">
                        30% energy saving
                      </td>
                      <td className="p-4 border-b border-primary-dark/10" data-id="element-1721">
                        Standard consumption
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1722">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1723">Brand FAQs</h3>
                <div className="space-y-4" data-id="element-1724">
                  {[{
                  q: 'Is upgrading to Daikin worth the initial cost?',
                  a: 'Yes, the energy savings alone typically provide a return on investment within 2 years, not including the savings on repairs and replacements.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1725">
                      <button onClick={() => setOpenFaqId(openFaqId === `daikin-${i}` ? null : `daikin-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1726">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1727">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1728">
                          {openFaqId === `daikin-${i}` ? <Minus className="w-5 h-5" data-id="element-1729" /> : <Plus className="w-5 h-5" data-id="element-1730" />}
                        </span>
                      </button>
                      {openFaqId === `daikin-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1731">
                          <p itemProp="text" data-id="element-1732">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 3 */}
            <section id="retail" className="mb-20 scroll-mt-32" data-id="element-1733">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1734">
                Choosing AC for retail Coventry
              </h2>
              <p data-id="element-1735">
                Retail environments require specific HVAC solutions. Systems
                must be quiet to avoid disrupting the shopping experience and
                aesthetically pleasing (e.g., concealed ducted units or sleek
                cassettes).
              </p>
              <p data-id="element-1736">
                Sizing is critical: retail spaces must account for high
                footfall, lighting heat, and open doors. If your total system
                exceeds 12kW, you will also need to consider{' '}
                <Link to="/knowledge-hub/compliance" data-id="element-1737">TM44 compliance</Link>.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1738">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1739">Retail AC FAQs</h3>
                <div className="space-y-4" data-id="element-1740">
                  {[{
                  q: 'What are the best brands for retail?',
                  a: 'Daikin and Toshiba are industry leaders for retail due to their reliability, quiet operation, and energy efficiency.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1741">
                      <button onClick={() => setOpenFaqId(openFaqId === `retail-${i}` ? null : `retail-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1742">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1743">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1744">
                          {openFaqId === `retail-${i}` ? <Minus className="w-5 h-5" data-id="element-1745" /> : <Plus className="w-5 h-5" data-id="element-1746" />}
                        </span>
                      </button>
                      {openFaqId === `retail-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1747">
                          <p itemProp="text" data-id="element-1748">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 4 */}
            <section id="toshiba" className="mb-20 scroll-mt-32" data-id="element-1749">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1750">Toshiba AC benefits</h2>
              <p data-id="element-1751">
                Toshiba is another premium brand we highly recommend. They offer
                excellent efficiency, quiet operation, and robust 10-year
                warranties when installed by an approved partner like AccuTemp.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1752">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1753">Toshiba FAQs</h3>
                <div className="space-y-4" data-id="element-1754">
                  {[{
                  q: 'How does Toshiba compare to Daikin?',
                  a: 'They are very similar in terms of build quality and reliability, though Toshiba can sometimes be slightly more cost-effective depending on the specific system requirements.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1755">
                      <button onClick={() => setOpenFaqId(openFaqId === `toshiba-${i}` ? null : `toshiba-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1756">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1757">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1758">
                          {openFaqId === `toshiba-${i}` ? <Minus className="w-5 h-5" data-id="element-1759" /> : <Plus className="w-5 h-5" data-id="element-1760" />}
                        </span>
                      </button>
                      {openFaqId === `toshiba-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1761">
                          <p itemProp="text" data-id="element-1762">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 5 */}
            <section id="why-us" className="mb-20 scroll-mt-32" data-id="element-1763">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1764">
                Why choose AccuTemp Coventry
              </h2>
              <p data-id="element-1765">
                With over 16 years of experience, AccuTemp is the leading
                commercial HVAC specialist in the West Midlands. As a Daikin D1+
                Premier Partner, we offer unparalleled expertise and extended
                warranties.
              </p>
              <p data-id="element-1766">
                Crucially, we handle all repairs and maintenance in-house. This
                means no 4-6 week delays waiting for manufacturer engineers—we
                resolve issues directly and quickly.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1767">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1768">AccuTemp FAQs</h3>
                <div className="space-y-4" data-id="element-1769">
                  {[{
                  q: 'Do you operate nationwide?',
                  a: 'Yes, while we are headquartered in Coventry, we manage commercial portfolios nationwide for FM companies and retail chains.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1770">
                      <button onClick={() => setOpenFaqId(openFaqId === `whyus-${i}` ? null : `whyus-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1771">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1772">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1773">
                          {openFaqId === `whyus-${i}` ? <Minus className="w-5 h-5" data-id="element-1774" /> : <Plus className="w-5 h-5" data-id="element-1775" />}
                        </span>
                      </button>
                      {openFaqId === `whyus-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1776">
                          <p itemProp="text" data-id="element-1777">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}